import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import {
  getColourProfile,
  deleteColourProfile,
  ColourProfile,
} from '../../api/colours';
import Button from '../../components/common/Button';
import Text from '../../components/common/Text';
import DetailContainer from '../../components/shared/DetailContainer';
import { useTheme } from '../../hooks/useTheme';
import { spacing, radii, shadows, fontSize, fontWeight } from '../../themes';

type ColourDetailRouteProp = RouteProp<RootStackParamList, 'ColourDetail'>;

const ColorBox = ({
  color,
  percentage,
  borderColor,
}: {
  color: string;
  percentage?: number;
  borderColor: string;
}) => (
  <View style={styles.colorBoxContainer}>
    <View style={[styles.colorBox, { backgroundColor: color, borderColor }]} />
    <View style={styles.colorInfo}>
      <Text size="xs" weight="medium" style={styles.colorHex}>
        {color}
      </Text>
      {percentage !== undefined && (
        <Text size="xs" variant="muted" style={styles.colorPercentage}>
          {Math.round(percentage * 100)}%
        </Text>
      )}
    </View>
  </View>
);

const ColourDetailScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<ColourDetailRouteProp>();
  const { colors } = useTheme();
  const {
    profileId,
    fromWardrobe = false,
    showWardrobeButton: _showWardrobeButton = false,
  } = route.params;

  const [profile, setProfile] = useState<ColourProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const data = await getColourProfile(profileId);
        setProfile(data);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load colour profile. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [profileId]);

  const handleDelete = useCallback(async () => {
    Alert.alert(
      'Delete Profile',
      'Are you sure you want to delete this colour profile?',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              setIsLoading(true);
              await deleteColourProfile(profileId);
              navigation.navigate('Home');
            } catch (err) {
              console.error('Error deleting profile:', err);
              setError('Failed to delete profile. Please try again.');
              setIsLoading(false);
            }
          },
        },
      ]
    );
  }, [profileId, navigation]);

  useEffect(() => {
    if (profile) {
      navigation.setOptions({
        headerShown: true,
        header: () => {
          const AppToolbar =
            require('../../components/shared/AppToolbar').default;
          return (
            <AppToolbar
              showBackButton={true}
              title={profile.name}
              rightButton={{
                icon: 'trash',
                onPress: handleDelete,
              }}
            />
          );
        },
      });
    }
  }, [navigation, profile, handleDelete]);

  if (isLoading) {
    return (
      <View
        style={[
          styles.loadingContainer,
          { backgroundColor: colors.background },
        ]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text size="md" variant="secondary" style={styles.loadingText}>
          Loading profile...
        </Text>
      </View>
    );
  }

  if (error || !profile) {
    return (
      <View
        style={[styles.errorContainer, { backgroundColor: colors.background }]}
      >
        <Text size="md" variant="error" style={styles.errorText}>
          {error || 'Profile not found'}
        </Text>
        <Button title="Go Back" onPress={() => navigation.goBack()} />
      </View>
    );
  }

  return (
    <DetailContainer style={{ backgroundColor: colors.background }}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {/* Dominant Colors */}
        <View style={[styles.section, { borderBottomColor: colors.border }]}>
          <Text size="xl" weight="bold" style={styles.sectionTitle}>
            Dominant Colors
          </Text>
          <Text size="sm" variant="secondary" style={styles.sectionSubtitle}>
            The main colors extracted from your image
          </Text>
          <View style={styles.colorsGrid}>
            {profile.dominant_colours.map((color, index) => (
              <ColorBox
                key={index}
                color={color.hex}
                percentage={color.percentage}
                borderColor={colors.borderLight}
              />
            ))}
          </View>
        </View>

        {/* Color Palette */}
        <View style={[styles.section, { borderBottomColor: colors.border }]}>
          <Text size="xl" weight="bold" style={styles.sectionTitle}>
            Colour Palette
          </Text>
          <Text size="sm" variant="secondary" style={styles.sectionSubtitle}>
            {profile.palette.type} palette based on your dominant colors
          </Text>
          <View style={styles.colorsGrid}>
            {profile.palette.colours.map((color, index) => (
              <ColorBox
                key={index}
                color={color.hex}
                borderColor={colors.borderLight}
              />
            ))}
          </View>
        </View>

        {/* Complementary Colors */}
        <View style={[styles.section, { borderBottomColor: colors.border }]}>
          <Text size="xl" weight="bold" style={styles.sectionTitle}>
            Complementary Colors
          </Text>
          <Text size="sm" variant="secondary" style={styles.sectionSubtitle}>
            Colors that pair well with your dominant colors
          </Text>
          <View style={styles.complementaryColorsContainer}>
            {(profile.recommendations.complementary_colours ?? []).map(
              (pair, index) => (
                <View key={index} style={styles.complementaryPair}>
                  <View style={styles.pairContainer}>
                    <View
                      style={[
                        styles.pairColor,
                        { backgroundColor: pair.original },
                      ]}
                    />
                    <View style={styles.pairArrow}>
                      <Text
                        size="xl"
                        variant="secondary"
                        style={styles.pairArrowText}
                      >
                        →
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.pairColor,
                        { backgroundColor: pair.complementary },
                      ]}
                    />
                  </View>
                  <View style={styles.pairLabels}>
                    <Text
                      size="xs"
                      style={[styles.pairLabel, { color: colors.text }]}
                    >
                      {pair.original}
                    </Text>
                    <Text
                      size="xs"
                      style={[styles.pairLabel, { color: colors.text }]}
                    >
                      {pair.complementary}
                    </Text>
                  </View>
                </View>
              )
            )}
          </View>
        </View>

        {/* Clothing Suggestions */}
        {profile.recommendations.clothing_suggestions.length > 0 && (
          <View style={[styles.section, { borderBottomColor: colors.border }]}>
            <Text size="xl" weight="bold" style={styles.sectionTitle}>
              Clothing Suggestions
            </Text>
            <Text size="sm" variant="secondary" style={styles.sectionSubtitle}>
              Recommendations based on your colour profile
            </Text>
            <View style={styles.suggestionsList}>
              {profile.recommendations.clothing_suggestions.map(
                (suggestion, index) => (
                  <View
                    key={index}
                    style={[
                      styles.suggestionItem,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.border,
                      },
                    ]}
                  >
                    <Text size="sm" style={{ color: colors.text }}>
                      {suggestion}
                    </Text>
                  </View>
                )
              )}
            </View>
          </View>
        )}

        {/* Next Step: Build Your Wardrobe */}
        <View style={styles.section}>
          <Text size="xl" weight="bold" style={styles.sectionTitle}>
            Ready to Build Your Wardrobe?
          </Text>
          <Text size="sm" variant="secondary" style={styles.sectionSubtitle}>
            Now that you know your colors, add your clothing items to get
            AI-powered outfit suggestions that perfectly match your palette!
          </Text>

          <View style={styles.actionContainer}>
            <Button
              title={
                fromWardrobe
                  ? 'Continue Building Your Wardrobe'
                  : 'Add Clothing Items'
              }
              onPress={() =>
                navigation.navigate('MainTabs', {
                  initialTab: 'SmartWardrobe',
                  autoShowAddForm: true,
                })
              }
              style={
                fromWardrobe
                  ? [
                      styles.emphasizedButton,
                      { backgroundColor: colors.primary },
                    ]
                  : styles.primaryButton
              }
              icon="shirt"
            />

            <TouchableOpacity
              style={styles.secondaryAction}
              onPress={() => navigation.navigate('Home')}
            >
              <Text size="md" weight="medium" variant="primary">
                Back to Home
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </DetailContainer>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing[4],
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[6],
  },
  errorText: {
    marginBottom: spacing[4],
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: spacing[6],
    borderBottomWidth: 1,
  },
  sectionTitle: {
    marginBottom: spacing[2],
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold,
  },
  sectionSubtitle: {
    marginBottom: spacing[4],
    fontSize: fontSize.base,
  },
  colorsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -spacing[2],
  },
  colorBoxContainer: {
    width: '33.333%',
    padding: spacing[2],
  },
  colorBox: {
    height: 80,
    borderRadius: radii.md,
    borderWidth: 1,
    marginBottom: spacing[2],
    ...shadows.xs,
  },
  colorInfo: {
    alignItems: 'center',
  },
  colorHex: {
    textAlign: 'center',
  },
  colorPercentage: {
    marginTop: 2,
    textAlign: 'center',
  },
  complementaryColorsContainer: {
    marginTop: spacing[2],
  },
  complementaryPair: {
    marginBottom: spacing[4],
  },
  pairContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing[2],
  },
  pairColor: {
    width: 80,
    height: 80,
    borderRadius: radii.md,
  },
  pairArrow: {
    paddingHorizontal: spacing[4],
  },
  pairArrowText: {
    fontSize: fontSize.xl,
  },
  pairLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing[2],
  },
  pairLabel: {
    fontSize: fontSize.xs,
  },
  suggestionsList: {
    marginTop: spacing[2],
  },
  suggestionItem: {
    padding: spacing[4],
    borderRadius: radii.md,
    marginBottom: spacing[2],
    borderWidth: 1,
  },
  actionContainer: {
    marginTop: spacing[4],
  },
  primaryButton: {
    marginBottom: spacing[3],
  },
  emphasizedButton: {
    marginBottom: spacing[3],
    transform: [{ scale: 1.02 }],
    ...shadows.lg,
  },
  secondaryAction: {
    alignItems: 'center',
    paddingVertical: spacing[3],
  },
});

export default ColourDetailScreen;
